import { z } from "zod";
/*
 * Wire contracts for the Heartbeat decision graph and its tuning.
 *
 * The graph is served rather than shipped to the client as a package, because
 * the engineering dashboard runs on Cloudflare and the manifest lives with the
 * engine. Serving it means the picture cannot be a release behind the pipeline
 * it claims to describe.
 */
export const HeartbeatGraphSection = z.object({
    id: z.string(),
    order: z.number(),
    label: z.string(),
    purpose: z.string(),
    /** Metadata for a conceptual section that has no implementation phase. */
    meta: z.string().optional(),
    /** How the preceding outcome contributes to this longer-horizon section. */
    relationship: z.string().optional(),
    /** Honest placeholder when the program has not defined this outcome yet. */
    emptyState: z.string().optional(),
});
export const HeartbeatGraphStageGroup = z.object({
    id: z.string(),
    order: z.number(),
    label: z.string(),
    purpose: z.string(),
});
/*
 * An implementation phase inside a framework section, with what the
 * picture needs to draw it folded. Served rather than held client-side: a
 * phase the client does not know renders its steps loose, outside the
 * hierarchy, and nothing says so.
 */
export const HeartbeatGraphStage = z.object({
    id: z.string(),
    order: z.number(),
    section: z.string(),
    phase: z.number(),
    label: z.string(),
    purpose: z.string(),
    groups: z.array(HeartbeatGraphStageGroup),
});
export const HeartbeatGraphNode = z.object({
    id: z.string(),
    label: z.string(),
    /** Phosphor icon name, in the React package's PascalCase spelling. */
    icon: z.string(),
    kind: z.string(),
    stage: z.string(),
    /** Factor group within tailoring variables. */
    group: z.string().optional(),
    summary: z.string(),
    /**
     * `code` or `model`. The distinction: code nodes are
     * deterministic and the model can neither see nor override them.
     */
    authority: z.enum(["code", "model"]),
    /** For model nodes, the `llm.request` agent tag, so a reader can find the span. */
    modelAgent: z.string().optional(),
    /** For decisions, the question the branch asks. Answers ride the edges. */
    question: z.string().optional(),
    /**
     * The answer that ends the tick here. Derived from the edges server-side, so
     * the picture cannot state a polarity the graph does not have. Absent on a
     * branch that ends no tick, workday mode being the one that asks and surfaces.
     */
    stops: z.string().optional(),
    /** Hard gates a lens may raise. Absent means it can only add narrative. */
    gates: z.array(z.string()).optional(),
});
export const HeartbeatGraphEdge = z.object({
    from: z.string(),
    to: z.string(),
    label: z.string().optional(),
    /** Written by this tick, read by a later one. Not a step in the sequence. */
    feedback: z.boolean().optional(),
});
export const HeartbeatTunable = z.object({
    key: z.string(),
    node: z.string(),
    label: z.string(),
    summary: z.string(),
    kind: z.enum(["boolean", "integer", "number"]),
    unit: z.string().optional(),
    default: z.union([z.number(), z.boolean()]),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    /** What the engine is running on right now: the default, or a tuned value. */
    value: z.union([z.number(), z.boolean()]),
    /** True when `value` came from a stored revision rather than the manifest. */
    tuned: z.boolean(),
});
export const HeartbeatTuningRevisionSummary = z.object({
    id: z.string(),
    createdAt: z.string(),
    /** GitHub login of the person who made the change. Never a shared bot. */
    createdBy: z.string(),
    note: z.string(),
    /** Only what differs from the manifest defaults. */
    values: z.record(z.string(), z.union([z.number(), z.boolean()])),
    parentId: z.string().nullable(),
});
/*
 * A route a tick can take, for the replay. Derived from the graph on the server
 * so the animation cannot walk a path the engine no longer has.
 */
export const HeartbeatTickPath = z.object({
    id: z.string(),
    label: z.string(),
    because: z.string(),
    /** Node ids in the order they are lit. */
    nodes: z.array(z.string()),
    /** True when the tick ends without surfacing. Derived from the walk. */
    endsQuiet: z.boolean(),
});
export const HeartbeatGraphResponse = z.object({
    environment: z.string(),
    /**
     * What this graph is and, more importantly, what it is not. Carried on the
     * wire so the surface cannot render the picture without the caveat.
     */
    scope: z.object({ title: z.string(), caveat: z.string() }),
    /** JITAI elements and Heartbeat delivery boundary, in conceptual order. */
    sections: z.array(HeartbeatGraphSection),
    /** Heartbeat implementation phases, in run order. Every node names one. */
    stages: z.array(HeartbeatGraphStage),
    nodes: z.array(HeartbeatGraphNode),
    edges: z.array(HeartbeatGraphEdge),
    tunables: z.array(HeartbeatTunable),
    /** The routes a tick can take, for the replay. */
    paths: z.array(HeartbeatTickPath),
    /** Null when the fleet is running on the manifest defaults. */
    revision: HeartbeatTuningRevisionSummary.nullable(),
    /**
     * Empty for any configuration that can reach somebody. The apply route
     * refuses a change that would silence the fleet, so this is only ever
     * non-empty for a revision that arrived some other way: written before that
     * guard existed, restored from a backup, or inserted by hand. Carried so the
     * page can say so, because such a fleet keeps ticking and every other gate
     * on it reports green.
     */
    silencing: z.array(z.object({ reason: z.string(), keys: z.array(z.string()) })),
});
export const HeartbeatTuningHistoryResponse = z.object({
    environment: z.string(),
    revisions: z.array(HeartbeatTuningRevisionSummary),
});
/*
 * No `environment` anywhere in this file. A fleet is an address, not a
 * parameter: each core-api reads and writes its own database, so naming a
 * different one on the wire could only ever write a row the fleet it names
 * will never read. The caller reaches staging by asking staging. Responses
 * still carry the environment, so a page can label what it is showing.
 */
export const HeartbeatFlowQuery = z.object({
    /**
     * How far back the counts reach. Clamped rather than refused: this is an
     * operator poking at a URL, and a window wider than the table has is a
     * harmless ask that should return the whole table, not a 400. Anything
     * unreadable falls back to the default week.
     */
    days: z.coerce
        .number()
        .transform((days) => Math.min(Math.max(1, days), 90))
        .catch(7),
});
export const CreateHeartbeatTuningRequest = z.object({
    /**
     * Required and non-trivial: a threshold change without a reason is
     * unreviewable, and this is the only record of intent the change will ever
     * have. Bounded so it stays a note rather than a document.
     */
    note: z.string().trim().min(8).max(500),
    /**
     * The full set of intended overrides, not a patch. Sending the whole set
     * makes a change idempotent and makes "reset this back to the default" an
     * ordinary edit (omit the key) rather than a second verb.
     */
    values: z.record(z.string(), z.union([z.number(), z.boolean()])),
    /**
     * The revision the editor was looking at. Rejected if it is no longer the
     * newest, so two people tuning at once cannot silently overwrite each other.
     */
    expectedRevisionId: z.string().nullable(),
});
export const CreateHeartbeatTuningResponse = z.object({
    revision: HeartbeatTuningRevisionSummary,
});
/*
 * Counts only, deliberately. See apps/core-api/src/lib/heartbeat-flow.ts: the
 * per-member trace carries clinical guidance and the member's own words, and
 * the surface that reads this is outside the HIPAA audit boundary.
 */
export const HeartbeatFlowResponse = z.object({
    environment: z.string(),
    windowDays: z.number(),
    decisions: z.number(),
    /** Ticks that ended at each node, keyed by graph node id. */
    endedAt: z.record(z.string(), z.number()),
    /** Ticks that got as far as each of the later nodes, keyed by graph node id. */
    reached: z.record(z.string(), z.number()),
    /** Ticks each read asked for silence on, keyed by read node id. */
    gatedBy: z.record(z.string(), z.number()),
});
//# sourceMappingURL=heartbeat-tuning.js.map